/**
 * Centralized Branding Configuration
 * Fetches from API and caches locally
 */

export interface BrandingConfig {
  logoUrl: string;
  logoAlt: string;
  appName: string;
  tagline: string;
  faviconUrl?: string;
  fontFamily?: string;
}

export interface SystemBranding {
  app_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  tagline: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  default_theme: string;
  font_family?: string | null;
}

// Default branding configuration
export const defaultBranding: BrandingConfig = {
  logoUrl: '/neimus-logo.png',
  logoAlt: 'NEIMUS360',
  appName: 'NEIMUS360',
  tagline: 'Environmental Intelligence Platform',
  faviconUrl: '/logo-icon.png',
  fontFamily: '',
};

const DEFAULT_FONT_STACK = "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_STACKS: Record<string, string> = {
  Inter: DEFAULT_FONT_STACK,
  Roboto: "'Roboto', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  'Open Sans': "'Open Sans', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  Lato: "'Lato', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  Poppins: "'Poppins', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
};

function getFontStack(fontFamily?: string): string {
  if (!fontFamily) return DEFAULT_FONT_STACK;
  return FONT_STACKS[fontFamily] || `${JSON.stringify(fontFamily)}, system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
}

export function applyBranding(config: BrandingConfig): void {
  document.documentElement.style.setProperty('--font-sans', getFontStack(config.fontFamily));
  document.documentElement.dataset.brandFont = config.fontFamily || 'system';
}

// Local storage key for custom branding
const BRANDING_STORAGE_KEY = 'neimus360_branding';
const BRANDING_CACHE_KEY = 'neimus360_branding_cache';
const BRANDING_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get current branding configuration
 * Checks localStorage for custom branding, falls back to default
 */
export function getBranding(): BrandingConfig {
  try {
    const stored = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultBranding, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load custom branding:', e);
  }
  return defaultBranding;
}

/**
 * Save custom branding configuration
 */
export function saveBranding(config: Partial<BrandingConfig>): void {
  try {
    const current = getBranding();
    const definedConfig = Object.fromEntries(
      Object.entries(config).filter(([, value]) => value !== undefined)
    ) as Partial<BrandingConfig>;
    const updated = { ...current, ...definedConfig };
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(updated));
    applyBranding(updated);
    
    // Update document title
    if (updated.appName) {
      document.title = updated.appName;
    }
    
    // Update favicon
    if (updated.faviconUrl) {
      updateFavicon(updated.faviconUrl);
    }
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('branding-updated', { detail: updated }));
  } catch (e) {
    console.error('Failed to save branding:', e);
  }
}

/**
 * Update the favicon dynamically
 */
function updateFavicon(url: string): void {
  // Normalize URL
  let faviconUrl = url.startsWith('http') || url.startsWith('/') 
    ? url 
    : `/api/v1${url}`;
  
  // Add cache-busting parameter
  const cacheBuster = `?v=${Date.now()}`;
  faviconUrl = faviconUrl + cacheBuster;
  
  // Determine file type for proper MIME type
  const isSvg = url.toLowerCase().includes('.svg');
  const isPng = url.toLowerCase().includes('.png');
  const isIco = url.toLowerCase().includes('.ico');
  
  const mimeType = isSvg ? 'image/svg+xml' : isPng ? 'image/png' : isIco ? 'image/x-icon' : 'image/png';
  
  // Remove all existing favicon links first
  const existingIcons = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
  existingIcons.forEach(icon => icon.remove());
  
  // Create new favicon link with proper type
  const iconLink = document.createElement('link');
  iconLink.rel = 'icon';
  iconLink.type = mimeType;
  iconLink.href = faviconUrl;
  document.head.appendChild(iconLink);
  
  // Also add shortcut icon for older browsers
  const shortcutIcon = document.createElement('link');
  shortcutIcon.rel = 'shortcut icon';
  shortcutIcon.type = mimeType;
  shortcutIcon.href = faviconUrl;
  document.head.appendChild(shortcutIcon);
  
  // Update apple-touch-icon
  let appleIconLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
  if (!appleIconLink) {
    appleIconLink = document.createElement('link');
    appleIconLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleIconLink);
  }
  appleIconLink.href = faviconUrl;
  
  console.log('Favicon updated to:', faviconUrl);
}

/**
 * Reset branding to defaults
 */
export function resetBranding(): void {
  localStorage.removeItem(BRANDING_STORAGE_KEY);
  localStorage.removeItem(BRANDING_CACHE_KEY);
  applyBranding(defaultBranding);
  window.dispatchEvent(new CustomEvent('branding-updated', { detail: defaultBranding }));
}

/**
 * Fetch system branding from API and apply it
 */
export async function fetchAndApplyBranding(): Promise<void> {
  try {
    applyBranding(getBranding());

    // Check cache first
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < BRANDING_CACHE_TTL) {
        applyBrandingFromApi(data);
        return;
      }
    }

    // Fetch from API
    const response = await fetch('/api/v1/settings/branding/system');
    if (response.ok) {
      const data: SystemBranding = await response.json();
      
      // Cache the result
      localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
      
      applyBrandingFromApi(data);
    }
  } catch (e) {
    console.warn('Failed to fetch system branding:', e);
    // Fall back to localStorage or defaults
    const branding = getBranding();
    applyBranding(branding);
    if (branding.appName) {
      document.title = branding.appName;
    }
  }
}

/**
 * Apply branding from API response
 */
function applyBrandingFromApi(data: SystemBranding): void {
  // Update document title
  if (data.app_name) {
    document.title = data.app_name;
  }
  
  // Update favicon
  if (data.favicon_url) {
    updateFavicon(data.favicon_url);
  }
  
  // Save to local storage for components to use
  const brandingUpdate: Partial<BrandingConfig> = {
    appName: data.app_name || 'NEIMUS360',
    logoUrl: data.logo_url || '/neimus-logo.png',
    tagline: data.tagline || 'Environmental Intelligence Platform',
    faviconUrl: data.favicon_url || undefined,
  };

  if (data.font_family !== undefined) {
    brandingUpdate.fontFamily = data.font_family || '';
  }

  saveBranding(brandingUpdate);
}
